import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IS_PUBLIC_FOLDER, IS_PUBLIC_OF_OVERQUOTA, PUBLIC_SHOVE } from 'reactApp/appHelpers/configHelpers';
import { hideOverquotaFeatures } from 'reactApp/appHelpers/featuresHelpers/features/hideOverquotaFeatures';
import { publicOverquota } from 'reactApp/appHelpers/featuresHelpers/features/publicOverquota';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import { isCloudOrMailOrApiOverQuota } from 'reactApp/modules/userQuota/overquota.helpers';
import { useRouteChangeProcessing } from 'reactApp/sections/MainPage/hooks/useRouteChangeProcessing';
import { DisableButton } from 'reactApp/ui/AdvBanners/DisableButton/DisableButton';
import { File } from 'reactApp/ui/Public/Components/File/File';
import { Folder } from 'reactApp/ui/Public/Components/Folder/Folder';
import { ShoveFrame } from 'reactApp/ui/Public/Components/Shove/ShoveFrame';

import styles from './ShovePublic.css';
import { getShoveBackground, sendShoveGa } from './ShovePublic.helpers';
import { EShoveType } from './ShovePublic.types';

interface Props {
    storage: EStorageType;
}

export const ShovePublic = memo(function ShovePublic({ storage }: Props): ReactElement {
    const shoveBackgroundStyle = PUBLIC_SHOVE ? getShoveBackground(PUBLIC_SHOVE) : '';
    const shoveStyle = shoveBackgroundStyle ? { background: shoveBackgroundStyle } : {};
    const type = IS_PUBLIC_FOLDER ? EShoveType.FOLDER : EShoveType.FILE;
    const showDisabledForOverquotaOwner = IS_PUBLIC_OF_OVERQUOTA && publicOverquota;

    const isCloudOrMailOrApiOverQuotaFlag = useSelector(isCloudOrMailOrApiOverQuota);
    // tempexp_19377-next-line
    const hideDisableButton = hideOverquotaFeatures?.disableAdButton?.public && isCloudOrMailOrApiOverQuotaFlag;

    useRouteChangeProcessing({
        storage,
        path: location.pathname.replace(`/${storage}/`, ''),
        search: location.search,
    });

    const onLoad = useCallback(() => {
        sendShoveGa(`${storage}_${type}_load`);
    }, []);

    const onTimeOut = useCallback(() => {
        sendShoveGa(`${storage}_${type}_timeout`);
    }, []);

    const onError = useCallback(() => {
        sendShoveGa(`${storage}_${type}_error`);
    }, []);

    useEffect(() => {
        sendShoveGa(`${storage}_${type}_show`);
    }, []);

    return (
        <div className={classNames(styles.root, styles[`root_type_${type}`])} style={{ background: PUBLIC_SHOVE?.color }}>
            <div className={styles.content} style={shoveStyle}>
                {IS_PUBLIC_FOLDER ? (
                    <div className={styles.folderContent}>
                        <Folder isShove />
                    </div>
                ) : (
                    <div className={classNames(styles.fileContent, { [styles.fileContent_overquota]: !!showDisabledForOverquotaOwner })}>
                        <File isShove />
                    </div>
                )}
            </div>
            <div className={styles.frame}>
                <ShoveFrame onLoad={onLoad} onTimeOut={onTimeOut} onError={onError} />
            </div>
            {!hideDisableButton && (
                <div className={styles.disableButton}>
                    <DisableButton id="shove" theme="dark" />
                </div>
            )}
        </div>
    );
});
